import { defineStore, storeToRefs } from 'pinia'
import { ref } from 'vue'

import { add, format } from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'

import { errorCodeMap } from '@/utils/commonInfoMap'

import { useUserDefaultSettingStore } from './user'
import { useAddEditZoneStore } from '@/store/addEditZone'
import { deepClone } from '@/utils/util'
import { mctRequest } from '@/api/axios'

export const useStatusStore = defineStore('userStatusSetting', () => {
  const userDefaultSettingStore = useUserDefaultSettingStore()
  const { timeZoneArea } = storeToRefs(userDefaultSettingStore)
  const { getLocalStorage } = userDefaultSettingStore

  const useAddEditZone = useAddEditZoneStore()
  const { editZoneDialogVisible, domainId4AddWebUrl, zoneType4WebUrl } =
    storeToRefs(useAddEditZone)

  const checkId = ref(0)
  const zoneId = ref(0)
  const zoneName = ref('')
  const clusterId = ref('0')
  const clusterName = ref('')
  const timeZone = ref(0)
  const duration = ref(getLocalStorage('STATUS_DURATION') || 8)
  const btnAbbrMap: { [key: number]: string } = {
    4: '4 hr',
    8: '8 hr',
    24: '24 hr',
    48: '2 d',
    168: '7 d'
  }
  const durationBtn = ref(btnAbbrMap[getLocalStorage('STATUS_DURATION') || 8])
  const endTime = ref(
    formatInTimeZone(
      add(new Date(), { hours: 1 }),
      timeZoneArea.value,
      'yyyy-MM-dd HH:00'
    )
    // formatInTimeZone(new Date(), timeZoneArea.value, 'yyyy-MM-dd HH:00')
  )
  const startTime = ref(
    formatInTimeZone(
      add(new Date(), { hours: -1 * duration.value + 1 }),
      timeZoneArea.value,
      'yyyy-MM-dd HH:00'
    )
  )
  const valueToFilterChecks = ref('')
  const isShowAllChecks = ref(!getLocalStorage('STATUS_ALERT_ONLY'))
  const isShowMaintainDialog = ref(false)
  const isShowSnoozeDialog = ref(false)
  const clickCheckName = ref('')
  const clickZoneType = ref(0)
  const timePos = ref(0)
  const serverId = ref(0)
  const hasModifyAccess = ref(true)

  const editCheckDialogVisible = ref(false)

  const isAllZoneExpanded = ref(true)

  const isClusterMaintained = ref(true)

  function changeDurationHours(newDuration: number) {
    duration.value = newDuration
    durationBtn.value = btnAbbrMap[newDuration]
    startTime.value = formatInTimeZone(
      add(new Date(), { hours: -1 * duration.value + 1 }),
      timeZoneArea.value,
      'yyyy-MM-dd HH:00'
    )
    endTime.value = formatInTimeZone(
      add(new Date(), { hours: 1 }),
      timeZoneArea.value,
      'yyyy-MM-dd HH:00'
    )
  }

  function changeStartTime(newStartTime: Date) {
    startTime.value = format(newStartTime, 'yyyy-MM-dd HH:00')
    endTime.value = format(
      add(new Date(newStartTime), { hours: duration.value }),
      'yyyy-MM-dd HH:00'
    )
  }
  function changeEndTime(newEndTime: Date) {
    startTime.value = format(
      add(new Date(newEndTime), { hours: -1 * duration.value }),
      'yyyy-MM-dd HH:00'
    )
    endTime.value = format(newEndTime, 'yyyy-MM-dd HH:00')
  }
  function changeEndTimeByDuration(duration: number) {
    startTime.value = format(
      add(new Date(startTime.value), { hours: duration }),
      'yyyy-MM-dd HH:00'
    )
    endTime.value = format(
      add(new Date(endTime.value), { hours: duration }),
      'yyyy-MM-dd HH:00'
    )
  }

  function showEditZoneDialog(zone: any) {
    zoneId.value = zone.id
    domainId4AddWebUrl.value = zone.domainId || 0
    zoneType4WebUrl.value = zone.type || 0
    editZoneDialogVisible.value = true
  }
  function showEditCheckDialog(zone: number, check: number) {
    zoneId.value = zone
    checkId.value = check
    editCheckDialogVisible.value = true
  }

  const defaultBlockBriefData = {
    failNum: 0,
    totalNum: 0,
    UTC: '',
    localTime: '',
    info: ''
  }
  const isShowBlockBriefDataPopover = ref(false)
  const triggerBlockBriefElement = ref()
  const triggerBlockBriefData = ref({
    failNum: 0,
    totalNum: 0,
    UTC: '',
    localTime: '',
    info: '',
    maintainInfo: '',
    startLoading: false
  })

  const selectedRowId = ref<number>(-1)
  const changeSelectedRowId = (checkId: number) => {
    selectedRowId.value = checkId
  }

  async function getMaintainInfo(checkId: any, startTime: any, isDot = false) {
    const blockDurationMap: any = {
      8: 30,
      24: 60,
      48: 120,
      168: 480
    }
    triggerBlockBriefData.value.startLoading = true
    if (isDot) {
      const config = {
        url: `/maintain/info/?serverid=${checkId}&time=${startTime}`,
        noLoading: true
      }
      const { maintainInfo } = await mctRequest.request<any>(config)
      triggerBlockBriefData.value.maintainInfo = maintainInfo
    } else {
      const endTime = (startTime + blockDurationMap[duration.value]) * 1000
      const config = {
        url: `/maintain/info/${checkId}/${startTime * 1000}/${endTime}`,
        noLoading: true
      }
      const { items } = await mctRequest.request<any>(config)
      triggerBlockBriefData.value.maintainInfo = items
    }
    triggerBlockBriefData.value.startLoading = false
  }

  let timer: any = null

  function showBlockBriefDate(event: any, block: any, isDot = false) {
    triggerBlockBriefData.value = deepClone(defaultBlockBriefData)
    if (isDot) {
      triggerBlockBriefData.value.failNum = -1
      triggerBlockBriefData.value.totalNum = -1
      const timePos = block?.timestamp || 0
      triggerBlockBriefData.value.UTC = formatInTimeZone(
        new Date(timePos),
        'UTC',
        'yyyy-MM-dd HH:mm:ss'
      )
      triggerBlockBriefData.value.localTime = format(
        new Date(timePos),
        'yyyy-MM-dd HH:mm:ss'
      )
      // @ts-ignore
      triggerBlockBriefData.value.info = errorCodeMap(block?.status)
      if (block.maintainStatus) {
        timer = setTimeout(() => {
          getMaintainInfo(block.serverId, triggerBlockBriefData.value.UTC, true)
        }, 1000)
      }
    } else {
      triggerBlockBriefData.value.failNum = block?.failNum || 0
      triggerBlockBriefData.value.totalNum = block?.totalNum || 0
      const timePos = block?.timePos || 0
      triggerBlockBriefData.value.UTC = formatInTimeZone(
        new Date(Number(timePos) * 1000),
        'UTC',
        'yyyy-MM-dd HH:mm'
      )
      triggerBlockBriefData.value.localTime = format(
        new Date(Number(timePos) * 1000),
        'yyyy-MM-dd HH:mm'
      )
      if (block.maintainStatus === 1) {
        timer = setTimeout(() => {
          getMaintainInfo(block.serverId, block?.timePos)
        }, 1000)
      }
    }

    triggerBlockBriefElement.value = event.target
    isShowBlockBriefDataPopover.value = true
  }

  function hideBlockBriefDate() {
    clearTimeout(timer)
    isShowBlockBriefDataPopover.value = false
  }

  const isShow2DayBlockPopover = ref(false)
  const trigger2DayBlockElement = ref()
  const trigger2DayBlockData = ref<any>({})
  function show2DayBlockDate(event: any, data: any) {
    trigger2DayBlockElement.value = event.target
    trigger2DayBlockData.value = data
    isShow2DayBlockPopover.value = true
  }

  return {
    checkId,
    zoneId,
    zoneName,
    clusterId,
    clusterName,
    timeZone,
    endTime,
    startTime,
    duration,
    durationBtn,
    valueToFilterChecks,
    isShowAllChecks,
    isShowSnoozeDialog,
    isShowMaintainDialog,
    clickCheckName,
    clickZoneType,
    timePos,
    serverId,
    hasModifyAccess,
    editCheckDialogVisible,
    isAllZoneExpanded,
    isClusterMaintained,
    selectedRowId,
    changeSelectedRowId,
    changeDurationHours,
    changeStartTime,
    changeEndTime,
    changeEndTimeByDuration,
    showEditZoneDialog,
    showEditCheckDialog,
    isShowBlockBriefDataPopover,
    triggerBlockBriefElement,
    triggerBlockBriefData,
    showBlockBriefDate,
    hideBlockBriefDate,
    isShow2DayBlockPopover,
    trigger2DayBlockElement,
    trigger2DayBlockData,
    show2DayBlockDate
  }
})
